var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
 * Copyright 2021, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { css, useTheme } from '@emotion/react';
import { Fragment, useEffect, useMemo, useRef, } from 'react';
import useLatest from 'use-latest';
import usePrevious from 'use-previous';
import { useFloating, flip, offset as offsetMiddleware, } from '@floating-ui/react-dom';
import isPropValid from '@emotion/is-prop-valid';
import { useClickTrigger } from '@sumup/collector';
import styled from '../../styles/styled';
import { listItem, shadow, typography } from '../../styles/style-mixins';
import { uniqueId } from '../../util/id';
import { useClickEvent } from '../../hooks/useClickEvent';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useFocusList } from '../../hooks/useFocusList';
import { isArrowDown, isArrowUp } from '../../util/key-codes';
import { useComponents } from '../ComponentsContext';
import Portal from '../Portal';
import Hr from '../Hr';
import { useStackContext } from '../StackContext';
import { isFunction } from '../../util/type-check';
const itemWrapperStyles = () => css `
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  text-align: left;
`;
const PopoverItemWrapper = styled('button', {
    shouldForwardProp: isPropValid,
})(listItem, itemWrapperStyles, typography('one'));
const iconStyles = (theme) => css `
  margin-right: ${theme.spacings.kilo};
`;
export const PopoverItem = (_a) => {
    var { children, icon: Icon, onClick, tracking } = _a, props = __rest(_a, ["children", "icon", "onClick", "tracking"]);
    const { Link } = useComponents();
    const handleClick = useClickEvent(onClick, tracking, 'popover-item');
    return (_jsxs(PopoverItemWrapper, Object.assign({ as: props.href ? Link : 'button', onClick: handleClick, role: "menuitem" }, props, { children: [Icon && _jsx(Icon, { css: iconStyles, size: "24" }), children] })));
};
const TriggerElement = styled.div `
  display: inline-block;
`;
const menuBaseStyles = ({ theme }) => css `
  padding: ${theme.spacings.byte} 0px;
  border: 1px solid var(--cui-border-subtle);
  box-sizing: border-box;
  border-radius: ${theme.borderRadius.byte};
  background-color: var(--cui-bg-elevated);
  visibility: hidden;
  opacity: 0;

  ${theme.mq.untilKilo} {
    opacity: 1;
    transform: translateY(100%);
    transition: transform ${theme.transitions.default},
      visibility ${theme.transitions.default};
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;
  }
`;
const menuOpenStyles = ({ theme, isOpen }) => isOpen &&
    css `
    opacity: 1;
    visibility: inherit;

    ${theme.mq.untilKilo} {
      transform: translateY(0);
    }
  `;
const PopoverMenu = styled('div')(shadow, menuBaseStyles, menuOpenStyles);
const dividerStyles = (theme) => css `
  margin: ${theme.spacings.byte} ${theme.spacings.mega};
  width: calc(100% - ${theme.spacings.mega}*2);
`;
const overlayStyles = ({ theme }) => css `
  ${theme.mq.untilKilo} {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: var(--cui-bg-overlay);
    visibility: hidden;
    opacity: 0;
    transition: opacity ${theme.transitions.default},
      visibility ${theme.transitions.default};
  }
`;
const overlayOpenStyles = ({ theme, isOpen }) => isOpen &&
    css `
    ${theme.mq.untilKilo} {
      visibility: inherit;
      opacity: 1;
    }
  `;
const Overlay = styled.div(overlayStyles, overlayOpenStyles);
const floatingStyles = ({ isOpen }) => css `
  pointer-events: ${isOpen ? 'all' : 'none'};
`;
const FloatingElement = styled.div(floatingStyles);
function isDivider(action) {
    return 'type' in action && action.type === 'divider';
}
export const Popover = (_a) => {
    var { isOpen = false, onToggle, actions, placement = 'bottom', fallbackPlacements = ['top', 'right', 'left'], component: Component, tracking, offset } = _a, props = __rest(_a, ["isOpen", "onToggle", "actions", "placement", "fallbackPlacements", "component", "tracking", "offset"]);
    const theme = useTheme();
    const zIndex = useStackContext();
    const triggerKey = useRef(null);
    const menuEl = useRef(null);
    const triggerId = useMemo(() => uniqueId('trigger_'), []);
    const menuId = useMemo(() => uniqueId('popover_'), []);
    const sendEvent = useClickTrigger();
    const { x, y, strategy, refs, update } = useFloating({
        placement,
        strategy: 'fixed',
        middleware: offset
            ? [offsetMiddleware(offset), flip({ fallbackPlacements })]
            : [flip({ fallbackPlacements })],
    });
    // This is a performance optimization to prevent event listeners from being
    // re-attached on every render.
    const floatingRef = useLatest(refs.floating.current);
    const focusProps = useFocusList();
    const prevOpen = usePrevious(isOpen);
    const isMobile = window.matchMedia(`${theme.breakpoints.untilKilo}`).matches;
    const mobileStyles = {
        position: 'fixed',
        bottom: '0px',
        left: '0px',
        right: '0px',
        width: 'auto',
        zIndex: zIndex || theme.zIndex.popover,
    };
    const handleToggle = (state) => {
        onToggle((prev) => {
            const next = isFunction(state) ? state(prev) : state;
            if (tracking && tracking.label) {
                sendEvent(Object.assign(Object.assign({ component: 'popover' }, tracking), { label: `${tracking.label}|${next ? 'open' : 'close'}` }));
            }
            return next;
        });
    };
    const handleTriggerClick = (event) => {
        // This prevents the event from bubbling which would trigger the
        // useClickOutside above and would prevent the popover from closing.
        event.stopPropagation();
        handleToggle((prev) => !prev);
    };
    const handleTriggerKeyDown = (event) => {
        if (isArrowDown(event)) {
            triggerKey.current = 'ArrowDown';
            handleToggle(true);
        }
        if (isArrowUp(event)) {
            triggerKey.current = 'ArrowUp';
            handleToggle((prev) => !prev);
        }
    };
    const handlePopoverItemClick = (event, onClick) => {
        if (onClick) {
            onClick(event);
        }
        handleToggle(false);
    };
    useEscapeKey(() => handleToggle(false), isOpen);
    useClickOutside(floatingRef, () => handleToggle(false), isOpen);
    useEffect(() => {
        /**
         * When we support `ResizeObserver` (https://caniuse.com/resizeobserver),
         * we can look into using Floating UI's `autoUpdate` (but we can't use
         * `whileElementInMounted` because our implementation hides the floating
         * element using CSS instead of using conditional rendering.
         * See https://floating-ui.com/docs/react-dom#updating
         */
        if (isOpen) {
            update();
            window.addEventListener('resize', update);
            window.addEventListener('scroll', update);
        }
        else {
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update);
        }
        // Focus the first or last element after opening
        if (!prevOpen && isOpen) {
            const element = (triggerKey.current && triggerKey.current === 'ArrowUp'
                ? menuEl.current && menuEl.current.lastElementChild
                : menuEl.current && menuEl.current.firstElementChild);
            if (element) {
                element.focus();
            }
        }
        // Focus the trigger button after closing
        if (prevOpen && !isOpen) {
            const triggerButton = (refs.reference.current &&
                refs.reference.current.firstElementChild);
            triggerButton.focus();
        }
        triggerKey.current = null;
        // Clean up the event listener when the component is unmounted
        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update);
        };
    }, [isOpen, prevOpen, refs.reference, update]);
    return (_jsxs(Fragment, { children: [_jsx(TriggerElement, Object.assign({ ref: refs.setReference }, { children: _jsx(Component, { id: triggerId, "aria-haspopup": true, "aria-controls": menuId, "aria-expanded": isOpen, onClick: handleTriggerClick, onKeyDown: handleTriggerKeyDown }) })), _jsxs(Portal, { children: [_jsx(Overlay, { isOpen: isOpen, style: { zIndex: zIndex || theme.zIndex.popover } }), _jsx(FloatingElement, Object.assign({}, props, { ref: refs.setFloating, isOpen: isOpen, style: isMobile
                            ? mobileStyles
                            : {
                                position: strategy,
                                top: y,
                                left: x,
                                zIndex: zIndex || theme.zIndex.popover,
                            } }, { children: _jsx(PopoverMenu, Object.assign({ id: menuId, ref: menuEl, isOpen: isOpen, "aria-labelledby": triggerId, role: "menu" }, { children: actions.map((action, index) => isDivider(action) ? (_jsx(Hr, { css: dividerStyles, role: "presentation" }, index)) : (_jsx(PopoverItem, Object.assign({}, action, focusProps, { onClick: (event) => handlePopoverItemClick(event, action.onClick) }), index))) })) }))] })] }));
};
